import React, { useEffect, useState } from 'react';
import { Outlet, Link } from 'react-router-dom';
import { useAuth } from '../../hooks';
import { Button } from '../common';
import { FiLogIn, FiUserPlus } from 'react-icons/fi';
import { ROUTES } from '../../utils/constants';
import { useResponsive } from '../../hooks';
import MobileMenu from './MobileMenu';

const MainLayout: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isMobile } = useResponsive();
  
  // Add padding to the body to account for the fixed header
  // and track scroll position for header blur effect
  useEffect(() => {
    // Calculate the header height
    const updateBodyPadding = () => {
      const headerHeight = document.getElementById('main-header')?.offsetHeight || 0;
      
      // Apply padding to the body
      document.body.style.paddingTop = `${headerHeight}px`;
    };
    
    // Track scroll position for header blur effect
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      if (scrollPosition > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    // Run once and also on window resize/scroll
    updateBodyPadding();
    window.addEventListener('resize', updateBodyPadding);
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('resize', updateBodyPadding);
      window.removeEventListener('scroll', handleScroll);
      document.body.style.paddingTop = '0';
    };
  }, []);
  
  return (
    <div className="min-h-screen flex flex-col">
      {/* Mobile Menu - Only for reference, not being opened anymore */}
      <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
      
      {/* Fixed position container for header */}
      <div className="fixed top-0 left-0 right-0 z-50">
        {/* Header/Navigation with extreme blur effect and light transparency */}
        <header 
          id="main-header" 
          className={`border-b border-white/10 transition-all duration-300 ${
            scrolled 
              ? 'shadow-lg' 
              : ''
          }`}
          style={{
            backgroundColor: scrolled ? 'rgba(12, 12, 12, 0.75)' : 'rgba(12, 12, 12, 0.65)',
            backdropFilter: `blur(${scrolled ? '30px' : '10px'})`,
            WebkitBackdropFilter: `blur(${scrolled ? '30px' : '10px'})`,
          }}
        >
          <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
            {/* Logo */}
            <Link to={ROUTES.HOME} className="flex items-center relative z-10">
              <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent">
                SpeedMeet.ai
              </span>
            </Link>
            
            {/* Empty space in the middle */}
            <div className="flex-1"></div>
            
            {/* Navigation - Desktop vs Mobile */}
            <div className="flex items-center space-x-2 sm:space-x-6 relative z-10">
              {/* Authentication Buttons */}
              {isAuthenticated ? (
                <Link to={ROUTES.DASHBOARD}>
                  <Button
                    variant="glass"
                    size={isMobile ? "xs" : "sm"}
                    glowEffect
                    leftIcon={<FiUserPlus />}
                  >
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <>
                  {/* Login button - Show on both mobile and desktop with icon */}
                  <Link to={ROUTES.LOGIN}>
                    <Button
                      variant="glass"
                      size={isMobile ? "xs" : "sm"}
                      leftIcon={<FiLogIn />}
                    >
                      Login
                    </Button>
                  </Link>
                  
                  {/* Sign Up button - Only show on desktop */}
                  {!isMobile && (
                    <Link to={ROUTES.SIGNUP}>
                      <Button
                        variant="primary"
                        size="sm"
                        glowEffect
                        leftIcon={<FiUserPlus />}
                      >
                        Sign Up
                      </Button>
                    </Link>
                  )}
                </>
              )}
              
              {/* Mobile menu button - REMOVED as requested */}
            </div>
          </div>
        </header>
      </div>
      
      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>
      
      {/* Footer - Updated with responsive grid */}
      <footer className="bg-background-glass backdrop-blur-sm border-t border-white/10">
        <div className="container mx-auto px-4 sm:px-6 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {/* Logo and Description */}
            <div className="col-span-1">
              <Link to={ROUTES.HOME} className="inline-flex items-center">
                <span className="text-xl font-bold bg-gradient-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent">
                  SpeedMeet.ai
                </span>
              </Link>
              <p className="mt-3 text-white/70 text-sm sm:text-base">
                The fastest way to meet, pitch, and hire in live one-on-one SpeedMeetings
              </p>
            </div>
            
            {/* Quick Links - Updated with actual links to legal pages */}
            <div>
              <h3 className="text-white font-medium mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm sm:text-base">
                <li>
                  <Link to={ROUTES.SERVICE_PROVIDERS} className="text-white/70 hover:text-primary-400 transition-colors">
                    For Freelancer & Agency
                  </Link>
                </li>
                <li>
                  <Link to={ROUTES.HOME} className="text-white/70 hover:text-primary-400 transition-colors">
                    For Hiring Managers
                  </Link>
                </li>
                <li>
                  <Link to={ROUTES.TERMS_OF_SERVICE} className="text-white/70 hover:text-primary-400 transition-colors">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to={ROUTES.PRIVACY_POLICY} className="text-white/70 hover:text-primary-400 transition-colors">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Copyright */}
          <div className="mt-8 pt-6 border-t border-white/10 text-center text-white/50 text-xs sm:text-sm">
            <p>© {new Date().getFullYear()} SpeedMeet.ai. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MainLayout;
